import { useState, useEffect } from 'react';
import OrbitModuleHeader from '../../shared/hub/OrbitModuleHeader.jsx';
import TasksPage from './pages/TasksPage.jsx';
import TaskDetailPage from './pages/TaskDetailPage.jsx';
import { VIEWS } from './lifecycle.js';

const BASE = '/orbit-development';

// The four views ARE the navigation. They used to be sub-tabs inside a single Tasks page, under
// a nav whose only other entry was Chains — two rows of tabs, the outer one with nothing to
// choose. Now each view is a page with its own URL, so a link goes where it says and a reload
// comes back to where you were.
//
// The order is the order work moves in: what is mine to do, what is coming, what is being
// checked, what is finished.
const NAV = VIEWS.map(v => ({ id: v.id, label: v.label, path: v.id }));

// pushState alone updates the URL without telling anyone. App.jsx's router and the workspace
// provider both listen for popstate, so the synthetic event is what actually navigates.
export function navigate(path, { replace = false } = {}) {
  if (replace) history.replaceState(null, '', path);
  else history.pushState(null, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

export const taskPath = (id) => `${BASE}/tasks/${id}`;
export const viewPath = (view) => `${BASE}/${view}`;

// The list a task was opened from.
//
// A task's own URL does not say which view you reached it through, and "back to the list" has
// to mean the list you were looking at — deleting a task from the Pipeline and being returned
// to My Work is being moved somewhere you did not ask to go, on top of an action that already
// changed the screen. So the last list rendered is remembered here.
//
// sessionStorage rather than a module variable so a reload on the task page still knows, and
// per-tab rather than persistent because it is about where you are right now, not a preference.
const LAST_VIEW_KEY = 'orbitdev:last-view';

export function rememberView(view) {
  if (!view) return;
  try { sessionStorage.setItem(LAST_VIEW_KEY, view); } catch { /* private mode */ }
}

export function listPath() {
  try {
    const last = sessionStorage.getItem(LAST_VIEW_KEY);
    // Checked against the real views: a stored id from an older build must not become a URL
    // that resolves to nothing.
    if (NAV.some(n => n.id === last)) return viewPath(last);
  } catch { /* fall through */ }
  return BASE;
}

function parsePath() {
  const p = window.location.pathname;
  const rest = p.startsWith(BASE) ? p.slice(BASE.length) : '/';

  const m = rest.match(/^\/tasks\/([^/]+)$/);
  if (m) return { view: 'task', taskId: m[1] };

  const slug = rest.replace(/^\//, '');
  if (NAV.some(n => n.id === slug)) return { view: 'list', list: slug };
  // The bare module path picks a view from the caller's role, once it is known. Until then
  // `list` is null and TasksPage renders nothing but its loading state.
  return { view: 'list', list: null };
}

export default function OrbitDevelopmentApp() {
  const [route, setRoute] = useState(parsePath);

  useEffect(() => {
    const onPop = () => setRoute(parsePath());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // A task detail page is reached from a list, and there is no one list it belongs to — so no
  // tab is lit while you are on it, rather than an arbitrary one being wrong.
  const activeTab = route.view === 'list' ? route.list : null;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--paper)' }}>
      <OrbitModuleHeader
        title="Orbit Development"
        moduleId="orbit-development"
        tabs={NAV}
        activeTab={activeTab}
        onTabChange={id => navigate(viewPath(id))}
      />
      {route.view === 'task' && <TaskDetailPage key={route.taskId} taskId={route.taskId} />}
      {route.view === 'list' && (
        <TasksPage
          view={route.list}
          onResolveView={v => navigate(viewPath(v), { replace: true })}
        />
      )}
    </div>
  );
}
